import { ExternalFileBrowserDialog } from "@/components/ExternalConnections/ExternalFileBrowserDialog";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { getExternalFolderLinkErrorMessage } from "@/lib/external-folder-link-error";
import { client as apiClient } from "@/lib/gen-api/client.gen";
import { queryKeys } from "@/lib/query-keys";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

interface LinkDriveFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  groupSlug: string;
  kitUuid: string;
}

export const LinkDriveFolderDialog = ({
  open,
  onOpenChange,
  groupUuid,
  groupSlug,
  kitUuid,
}: LinkDriveFolderDialogProps) => {
  const [linking, setLinking] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleFolderSelected = async (
    folders: {
      connectionUuid: string;
      externalId: string;
      name: string;
      driveId?: string;
    }[],
  ) => {
    if (folders.length === 0) return;
    const folder = folders[0];
    setLinking(true);
    try {
      const response = await apiClient.post<{
        folderUuid: string;
        externalFolderUuid: string;
      }>({
        url: `/api/v2/groups/${groupUuid}/kits/${kitUuid}/external-folders`,
        body: {
          connectionUuid: folder.connectionUuid,
          externalId: folder.externalId,
          driveId: folder.driveId,
        },
      });

      if (response.error) throw response.error;
      if (!response.data?.folderUuid) {
        throw new Error("Failed to link folder");
      }

      toast.success(`Folder "${folder.name}" linked successfully!`);
      onOpenChange(false);
      const linkedFolderUuid = response.data.folderUuid;
      await queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid, kitUuid),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid, kitUuid, linkedFolderUuid),
      });
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
        params: {
          groupSlug,
          kitUuid,
          folderUuid: linkedFolderUuid,
          _splat: "",
        },
      });
      // Parse jobs start shortly after link returns; nudge a refetch so
      // statuses move past "Uploaded" without a manual refresh.
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolder(groupUuid, kitUuid, linkedFolderUuid),
        });
      }, 1000);
    } catch (error) {
      toast.error(
        getExternalFolderLinkErrorMessage(
          error,
          `Failed to link folder "${folder.name}"`,
        ),
      );
      console.error("Failed to link external folder:", error);
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <ExternalFileBrowserDialog
        open={open && !linking}
        onOpenChange={onOpenChange}
        groupUuid={groupUuid}
        mode="folder"
        onSelected={handleFolderSelected}
      />
      <LoadingOverlay
        loading={linking}
        title="Linking Folder"
        description="Importing files from cloud..."
      />
    </>
  );
};
